import { useState } from "react";
import { MENU_ROOT_STATE } from "@/types/NavigationState";
import { useHistory } from "react-router-dom";
import { Collapse, Nav } from "react-bootstrap";
import type { MenuRoute, MenuSource } from "../Menus/menuTypes";
import { MenuSourceConst } from "../Menus/menuTypes";
import { ORG_ALL, SCOPE_PARAM } from "@/contexts/OrgContext";
import SntLabIcon from "@icons/SntLabIcon";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@hooks/useLanguage";

/**
 * Location object from react-router-dom
 */
interface Location {
  pathname: string;
  search?: string;
  hash?: string;
  state?: unknown;
}

/**
 * Extended MenuRoute interface with additional properties used in NavList
 */
export interface ExtendedMenuRoute extends MenuRoute {
  path?: string;
  redirect?: boolean;
  title?: string;
  param?: string;
  category?: string;
  reportType?: string;
  type?: string;
  background?: string;
  excludeSubPages?: string[];
  views?: ExtendedMenuRoute[];
  isLabFeature?: boolean;
}

/**
 * Props for the NavList component
 */
interface NavListProps {
  collapsible?: boolean;
  routes?: ExtendedMenuRoute[];
  location?: Location;
  orgId?: string;
  menuType?: string;
  /**
   * Which hook produced `routes`. Decides the org parameter each item links to:
   * own-level items append nothing, cross-level items append `scope=all` when no
   * org is sliced. See buildItemHref.
   */
  menuSource?: MenuSource;
}

/**
 * State for managing collapse states of menu items
 */
type CollapseState = Record<string, boolean>;

/**
 * Verifies if routeName is the one active (in browser input)
 */
export const activeRoute = (prop: ExtendedMenuRoute, location: Location): string => {
  let routeName = "";
  let to = prop.to;

  if (prop.path) {
    to = prop.path;
  }
  routeName = (prop.layout || "") + to;

  const subPages = prop.subPages || [];
  const excludeSubPages = prop.excludeSubPages || [];

  // Check if current path should be excluded (e.g., site maps shown in home menu)
  const isExcluded = excludeSubPages.some((excludePath) => location.pathname === excludePath);

  // Check exact match for main route, or if pathname matches subPage (exact or with path params)
  const isSubPageActive =
    !isExcluded &&
    subPages.some((subPage) => location.pathname === subPage || location.pathname.startsWith(subPage + "/"));
  return location.pathname === routeName || isSubPageActive ? "active" : "";
};

export const expanded = (prop: ExtendedMenuRoute, location: Location): boolean => {
  return prop.views?.find((r) => r.to === location.pathname) ? true : false;
};

export const expandedCheckSubTab = (prop: ExtendedMenuRoute, location: Location): boolean => {
  return prop?.views?.find((r) =>
    r?.subPages?.find((x) => x === location.pathname || location.pathname.startsWith(x + "/"))
  )
    ? true
    : false;
};

/** What buildMenuItemHref needs to know about the menu an item belongs to. */
export interface MenuHrefContext {
  menuSource?: MenuSource;
  orgId?: string;
  menuType?: string;
}

/**
 * The URL a sidebar item navigates to.
 *
 * Two parameters carry the scope: `org=<id>` names the organisation, `scope=all`
 * names the level, and the absence of both is own level. So an own-level item
 * appends nothing, while a cross-level item must append something — `org=<id>`
 * when one is sliced, `scope=all` when none is, or it would land on own level.
 *
 * `orgId` here is the LEVEL's org (`SidebarBody.levelOrgId`), not the url's: at
 * All it is null even while the url names the org of an open detail, so these
 * links take the `scope=all` branch instead of slicing that record's org.
 *
 * Module-level so the menu search can open its first match through exactly the
 * link the row itself would carry.
 */
export const buildMenuItemHref = (
  prop: ExtendedMenuRoute,
  { menuSource, orgId, menuType }: MenuHrefContext
): string => {
  const prefix = prop.layout || "";
  const extra = prop.param ? `&${prop.param}` : "";

  // Own level is the absence of the param, so there is nothing to append.
  if (menuSource === MenuSourceConst.OWN) {
    return `${prefix}${prop.to}${prop.param ? `?${prop.param}` : ""}`;
  }
  if (orgId && menuType !== "MAIN") {
    return `${prefix}${prop.to}?org=${orgId}${extra}`;
  }
  // Cross level with nothing sliced still has to say so explicitly, or the
  // link would drop to own level. `scope=all` names the LEVEL — the old
  // `org=all` spelling is still read for existing bookmarks, but nothing writes
  // it any more (STIC-15790).
  if (menuSource === MenuSourceConst.CROSS && menuType !== "MAIN") {
    return `${prefix}${prop.to}?${SCOPE_PARAM}=${ORG_ALL}${extra}`;
  }
  // A dashboard link used to carry the organisation a WORKSPACE dashboard was
  // last opened for, read from localStorage. It went around `updateOrg`, which is
  // the only place that knows when naming an organisation would move the sidebar
  // — so a value left over from weeks ago kept sending users to another level
  // straight from the menu. The dashboard page remembers its own selection now
  // (PowerBiWorkSpaceDashBoard) without rewriting anyone's links.
  return `${prefix}${prop.to}`;
};

function NavList({
  collapsible = true,
  routes = [],
  location = { pathname: "" } as Location,
  orgId,
  menuType,
  menuSource,
}: NavListProps) {
  const history = useHistory();

  const buildItemHref = (prop: ExtendedMenuRoute): string =>
    buildMenuItemHref(prop, { menuSource, orgId, menuType });

  // Handle navigation with deferred execution to prevent DOM race conditions
  const handleNavigation = (e: React.MouseEvent, targetPath: string) => {
    e.preventDefault();
    // Use requestAnimationFrame to defer navigation until after current DOM operations complete
    requestAnimationFrame(() => {
      // menuRoot: a sidebar-menu click starts a fresh navigation root —
      // AppLayout resets the back-arrow history depth so top-level pages
      // never show the arrow, only drill-ins from here do (STIC-15588)
      history.push(targetPath, MENU_ROOT_STATE);
    });
  };

  // this verifies if any of the collapses should be default opened on a rerender of this component
  // for example, on the refresh of the page,
  // while on the src/views/forms/RegularForms.jsx - route /regular-forms
  const getCollapseInitialState = (routes: ExtendedMenuRoute[]): boolean => {
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse && routes[i].views && getCollapseInitialState(routes[i].views || [])) {
        return true;
      } else if (location && location.pathname === (routes[i].layout || "") + routes[i].to) {
        return true;
      } else if (routes[i].path && location && location.pathname === (routes[i].layout || "") + routes[i].path) {
        return true;
      }
    }
    return false;
  };

  // this creates the intial state of this component based on the collapse routes
  // that it gets through routes prop
  const getCollapseStates = (routes: ExtendedMenuRoute[]): CollapseState => {
    let initialState: CollapseState = {};
    routes.map((prop) => {
      if (prop.collapse && prop.state) {
        initialState = {
          [prop.state]: getCollapseInitialState(prop.views || []),
          ...getCollapseStates(prop.views || []),
          ...initialState,
        };
      }
      return null;
    });
    return initialState;
  };

  // this is for the rest of the collapses
  const [state, setState] = useState<CollapseState>(getCollapseStates(routes));

  // this function creates the links and collapses that appear in the sidebar (left menu)
  const createLinksDashboard = (routes: ExtendedMenuRoute[]) => {
    const organisationDashboards: ExtendedMenuRoute[] = [];
    const workspaceDashboards: ExtendedMenuRoute[] = [];
    const normalDashboards: ExtendedMenuRoute[] = [];
    const otherDashboards: ExtendedMenuRoute[] = [];
    const topMenu: ExtendedMenuRoute[] = [];

    for (let index = 0; index < routes.length; index++) {
      const element = routes[index];
      if (element.reportType === "ORGANIZATION") {
        organisationDashboards.push(element);
      } else if (element.reportType === "WORKSPACE") {
        workspaceDashboards.push(element);
      } else if (element.reportType === "NORMAL") {
        normalDashboards.push(element);
      } else if (element.type === "bi") {
        otherDashboards.push(element);
      } else {
        topMenu.push(element);
      }
    }
    return (
      <>
        {createLinks(topMenu)}
        {topMenu.length > 0 &&
          (organisationDashboards.length > 0 ||
            normalDashboards.length > 0 ||
            workspaceDashboards.length > 0 ||
            otherDashboards.length > 0) && (
            <hr
              style={{
                color: "lime",
                height: "1px",
                margin: "10px 10px 10px 40px",
              }}
            />
          )}
        {createLinks(organisationDashboards)}
        {organisationDashboards.length > 0 &&
          (normalDashboards.length > 0 || workspaceDashboards.length > 0 || otherDashboards.length > 0) && (
            <hr
              style={{
                color: "lime",
                height: "1px",
                margin: "10px 10px 10px 40px",
              }}
            />
          )}
        {createLinks(normalDashboards)}
        {normalDashboards.length > 0 && (workspaceDashboards.length > 0 || otherDashboards.length > 0) && (
          <hr
            style={{
              color: "lime",
              height: "1px",
              margin: "10px 10px 10px 40px",
            }}
          />
        )}
        {createLinks(workspaceDashboards)}
        {workspaceDashboards.length > 0 && otherDashboards.length > 0 && (
          <hr
            style={{
              color: "lime",
              height: "1px",
              margin: "10px 10px 10px 40px",
            }}
          />
        )}
        {createLinks(otherDashboards)}
      </>
    );
  };

  const createLinks = (routes: ExtendedMenuRoute[]) => {
    const rendered = createLinkItems(routes);

    /**
     * The engineering block that closes the SYSTEM own-level menu opens with a
     * heading, so the technical pages read as their own neighbourhood rather
     * than as more of the same (STIC-15790). Its position is resolved here, on
     * the pruned list NavList is handed, so the heading follows the first
     * section that is actually still visible.
     */
    const firstZoneIndex = routes.findIndex(
      (route) => route.engineeringZone === true
    );
    if (firstZoneIndex >= 0) {
      rendered.splice(
        firstZoneIndex,
        0,
        <li className="engineering-zone-header" key="engineering-zone-header">
          {translate(LanguageKey.engineering_tools_below_key)}
        </li>
      );
    }

    return rendered;
  };

  const createLinkItems = (routes: ExtendedMenuRoute[]) => {
    return routes.map((prop, key) => {
      if (prop.redirect) {
        return null;
      }
      const isLastTopOverview =
        prop.topOverview === true && (key === routes.length - 1 || routes[key + 1]?.topOverview !== true);
      if (prop.collapse) {
        const st: CollapseState = {};
        if (prop.state) {
          st[prop.state] = !state[prop.state];
        }
        return (
          <Nav.Item
            className={[
              getCollapseInitialState(prop.views || []) ? "active" : "",
              prop.engineeringZone ? "engineering-zone" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            as="li"
            key={key}
            style={{ background: prop.background }}
          >
            <Nav.Link
              className={
                (prop.state && state[prop.state]) ||
                !collapsible ||
                (location && expanded(prop, location)) ||
                (location && expandedCheckSubTab(prop, location))
                  ? "collapsed nav-category"
                  : "nav-category"
              }
              data-toggle="collapse"
              onClick={(e) => {
                e.preventDefault();
                setState({ ...state, ...st });
              }}
              aria-expanded={
                (prop.state && state[prop.state]) ||
                (location && expanded(prop, location)) ||
                (location && expandedCheckSubTab(prop, location))
              }
              style={{ cursor: "default" }}
            >
              {prop.icon}
              <p
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={prop.title || (typeof prop.name === "string" ? prop.name : undefined)}
              >
                {prop.name}
                {collapsible && <b className="caret"></b>}
              </p>
            </Nav.Link>
            <Collapse
              in={
                (prop.state && state[prop.state]) ||
                !collapsible ||
                (location && expanded(prop, location)) ||
                (location && expandedCheckSubTab(prop, location))
              }
            >
              <div>
                <Nav as="ul">
                  {prop.name === "Dashboards" ? createLinksDashboard(prop.views || []) : createLinks(prop.views || [])}
                </Nav>
              </div>
            </Collapse>
          </Nav.Item>
        );
      }
      return (
        <Nav.Item
          className={[
            location ? activeRoute(prop, location) : "",
            prop.topOverview ? "top-overview" : "",
            isLastTopOverview ? "top-overview-last" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          key={key}
          as="li"
        >
          <Nav.Link
            href={buildItemHref(prop)}
            onClick={(e) => handleNavigation(e, buildItemHref(prop))}
            // Rule #5 (STIC-15534/STIC-15615 R9+R11): level-unavailable
            // items (the hook decided they don't apply at the selected
            // level) stay visible but greyed — never hidden, still fully
            // clickable/navigable
            className={prop.disabled ? "opacity-50" : undefined}
            style={{
              cursor: "pointer",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={prop.title || (typeof prop.name === "string" ? prop.name : undefined)}
          >
            {prop.topOverview && <span className="top-overview-icon-wrap">{prop.icon}</span>}
            {prop.name && (
              <>
                <span className="sidebar-normal">
                  {prop.name}
                  {prop.isLabFeature && (
                    <SntLabIcon
                      className="ms-2"
                      size={14}
                      style={{ marginTop: "-2px" }}
                      title={translate(LanguageKey.lab_feature_key)}
                    />
                  )}
                </span>
              </>
            )}
          </Nav.Link>
        </Nav.Item>
      );
    });
  };

  return <Nav as="ul">{createLinks(routes)}</Nav>;
}

export default NavList;
